import type { GradePublic } from '@repo/types/grades';
import type { GradeMapping } from '@repo/types/quiz';
import type { CSSProperties } from 'react';

import { gradeScaleColorAt } from '@/components/features/grade-scale/gradeScaleColors';
import { resolveGradeLabel, resolveGradePercent } from '@/components/features/grade-scale/resolveGrade';

/**
 * The parts of a grade needed to render one.
 *
 * Narrower than `GradePublic` so payloads that carry only the value can be
 * rendered too — the parent assignments endpoint trims its grades to
 * id/name/desc/valueType/value. Without an origin the grade simply resolves by
 * label rather than against a scale.
 */
export type GradeStyleInput = Pick<GradePublic, 'value' | 'valueType'> & Partial<Pick<GradePublic, 'originType' | 'quiz' | 'assignment'>>;

export interface ResolvedGradeBand {
  /** Band label, or the raw value ('%'-suffixed for percentage grades). */
  display: string;
  /** Band color or ramp position; null when nothing resolves. */
  color: string | null;
}

// Grades carry no scale snapshot — they render under the origin's *current*
// mapping, so editing a scale re-colors its existing grades.
function getOriginMapping(grade: GradeStyleInput): GradeMapping | null {
  if (grade.originType === 'quizResponse') {
    return grade.quiz?.gradeMapping ?? null;
  }
  if (grade.originType === 'assignmentResponse') {
    return grade.assignment?.gradeMapping ?? null;
  }
  return null;
}

/**
 * The percentage a grade carries, or null. Rows without a valueType (the
 * backend doesn't require it; legacy rows stored '96%') count only in the
 * unambiguous 'NN%' form — a bare numeric could be an int grade.
 */
export function gradePercentage(grade: GradeStyleInput): number | null {
  const raw = grade.value.trim();

  let text: string | null = null;
  if (grade.valueType === 'percentage') {
    text = raw === '' ? null : raw;
  } else if (grade.valueType == null) {
    text = /^\d+(?:\.\d+)?%$/.exec(raw) ? raw.slice(0, -1) : null;
  }
  if (text === null) return null;

  const parsed = Number(text);
  if (!Number.isFinite(parsed)) return null;
  return Math.min(Math.max(parsed, 0), 100);
}

/**
 * A percentage grade resolves by range into the origin's scale (or sits at
 * its own ramp position without one); anything else resolves by band label
 * only — a numeric label like '5' is never confused with 5%.
 */
export function resolveGradeBand(grade: GradeStyleInput): ResolvedGradeBand {
  const mapping = getOriginMapping(grade);
  const percentage = gradePercentage(grade);

  if (percentage !== null) {
    const band = resolveGradePercent(mapping, percentage);
    return {
      display: band.label ?? `${percentage}%`,
      color: band.color ?? gradeScaleColorAt(percentage / 100),
    };
  }

  const band = resolveGradeLabel(mapping, grade.value);
  return { display: band.label ?? grade.value, color: band.color };
}

export function getGradeDisplay(grade: GradeStyleInput): string {
  return resolveGradeBand(grade).display;
}

export function getGradeStyle(grade: GradeStyleInput): CSSProperties | undefined {
  const { color } = resolveGradeBand(grade);
  if (!color) return undefined;

  return {
    color,
    backgroundColor: `color-mix(in srgb, ${color} 12%, transparent)`,
    borderColor: `color-mix(in srgb, ${color} 32%, transparent)`,
  };
}
